/**
 * gez-product-information.js
 * Handles interactive behavior for the Product Information Section (gez-product-information.liquid).
 *
 * Responsibilities:
 *   1. Shipping countdown — computes HH:MM to cutoff, skips weekends,
 *      computes delivery date range, updates every minute.
 *   2. Gallery swipe / dot / thumbnail coordination.
 *   3. Kit size variant swap — on card click, update URL, swap gallery image,
 *      fire gez:variant-changed event, update price display.
 *   4. Pack quantity selector — visual selection state, emit data-tier-qty
 *      for Shopify Function consumption.
 *   5. Heart / favorite localStorage toggle.
 *
 * All DOM queries are scoped to section.id to support multiple instances.
 */

'use strict';

/* ============================================================
   1. SHIPPING COUNTDOWN
   ============================================================ */

class GezShippingCountdown {
  /**
   * @param {HTMLElement} el - [data-countdown] element
   */
  constructor(el) {
    this.el = el;
    this.cutoffHour  = parseInt(el.dataset.cutoffHour  ?? '14', 10);
    this.minDays     = parseInt(el.dataset.minDays     ?? '2',  10);
    this.maxDays     = parseInt(el.dataset.maxDays     ?? '6',  10);
    this.template    = el.dataset.labelTemplate ?? 'Order in {countdown} for delivery between {date_range}';
    this.textEl      = el.querySelector('.gez-pi__deliver-text');
    this._tick();
    this._interval = setInterval(() => this._tick(), 60_000);
  }

  destroy() {
    clearInterval(this._interval);
  }

  _tick() {
    const now         = new Date();
    const today       = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const cutoff      = new Date(today.getTime());
    cutoff.setHours(this.cutoffHour, 0, 0, 0);

    let dispatchDate;
    if (now < cutoff) {
      // Still before cutoff today — dispatches today (if weekday)
      dispatchDate = this._nextBusinessDay(today, 0);
    } else {
      // After cutoff — dispatches next business day
      dispatchDate = this._nextBusinessDay(today, 1);
    }

    const minDelivery = this._addBusinessDays(dispatchDate, this.minDays - 1);
    const maxDelivery = this._addBusinessDays(dispatchDate, this.maxDays - 1);

    const dateRange = this._formatDateRange(minDelivery, maxDelivery);
    const countdown = now < cutoff ? this._formatCountdown(cutoff - now) : '';

    let label;
    if (countdown) {
      label = this.template
        .replace('{countdown}', countdown)
        .replace('{date_range}', dateRange);
    } else {
      label = `Estimated delivery ${dateRange}`;
    }

    if (this.textEl) {
      this.textEl.textContent = label;
    }
  }

  /**
   * Returns the next business day on or after startDate + offsetDays.
   * Skips Saturday (6) and Sunday (0).
   * @param {Date} startDate
   * @param {number} offsetDays
   * @returns {Date}
   */
  _nextBusinessDay(startDate, offsetDays) {
    const d = new Date(startDate.getTime());
    d.setDate(d.getDate() + offsetDays);
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  /**
   * Adds n business days to a date.
   * @param {Date} startDate
   * @param {number} n
   * @returns {Date}
   */
  _addBusinessDays(startDate, n) {
    const d = new Date(startDate.getTime());
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
    }
    return d;
  }

  /** @param {number} ms - milliseconds remaining */
  _formatCountdown(ms) {
    const totalMinutes = Math.floor(ms / 60_000);
    const hours        = Math.floor(totalMinutes / 60);
    const minutes      = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  }

  /**
   * @param {Date} minDate
   * @param {Date} maxDate
   * @returns {string} e.g. "May 17 - May 21"
   */
  _formatDateRange(minDate, maxDate) {
    const opts = { month: 'short', day: 'numeric' };
    const locale = document.documentElement.lang || 'en-US';
    const minStr = minDate.toLocaleDateString(locale, opts);
    const maxStr = maxDate.toLocaleDateString(locale, opts);
    if (minStr === maxStr) return minStr;
    return `${minStr} - ${maxStr}`;
  }
}

/* ============================================================
   2. GALLERY COORDINATOR
   Phase 10: Slide-based show/hide pattern.
   Pre-rendered slides are toggled via --active class + hidden attribute.
   Supports image, video, external_video, model media types.
   Videos in non-active slides are paused automatically.
   Native fullscreen on <video> is provided by the browser controls button.
   ============================================================ */

class GezGalleryCoordinator {
  /**
   * @param {HTMLElement} wrapper - .gez-pi-wrapper
   */
  constructor(wrapper) {
    this.wrapper    = wrapper;
    this.slides     = Array.from(wrapper.querySelectorAll('.gez-pi-gallery__slide'));
    this.dots       = Array.from(wrapper.querySelectorAll('.gez-pi-gallery__dot'));
    this.thumbs     = Array.from(wrapper.querySelectorAll('.gez-pi-gallery__thumb'));
    this.currentIdx = 0;
    this.totalItems = this.slides.length;
    this.el         = wrapper.querySelector('.gez-pi-gallery') ?? wrapper;

    this._bindDots();
    this._bindThumbs();
    this._bindArrows();
    this._bindSwipe();
    this._bindKeyboard();
  }

  _bindDots() {
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.idx, 10);
        this.goTo(idx);
      });
    });
  }

  _bindThumbs() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.dataset.idx, 10);
        this.goTo(idx);
      });
    });
  }

  /** Advance to the next slide, wrapping around from last to first. */
  next() {
    if (this.totalItems < 2) return;
    this.goTo((this.currentIdx + 1) % this.totalItems);
  }

  /** Step back to the previous slide, wrapping around from first to last. */
  prev() {
    if (this.totalItems < 2) return;
    this.goTo((this.currentIdx - 1 + this.totalItems) % this.totalItems);
  }

  /** Bind click handlers on [data-gallery-arrow] buttons. */
  _bindArrows() {
    const prevBtn = this.wrapper.querySelector('[data-gallery-arrow="prev"]');
    const nextBtn = this.wrapper.querySelector('[data-gallery-arrow="next"]');
    if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
    if (nextBtn) nextBtn.addEventListener('click', () => this.next());
  }

  /**
   * Bind touch swipe on [data-gallery-swipeable] frame.
   * 50px horizontal threshold to navigate; 60px vertical movement cancels.
   * passive: true so we never block scroll.
   * Swipe is intentionally disabled while a video is playing to avoid
   * conflicting with the video player's own touch handling.
   */
  _bindSwipe() {
    const frame = this.wrapper.querySelector('[data-gallery-swipeable]');
    if (!frame) return;

    let startX = 0;
    let startY = 0;
    let active = false;
    const THRESHOLD          = 50;
    const VERTICAL_TOLERANCE = 60;

    frame.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      // Do not initiate swipe if a video in the active slide is playing
      const activeSlide = this.slides[this.currentIdx];
      if (activeSlide) {
        const vid = activeSlide.querySelector('video');
        if (vid && !vid.paused) return;
      }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      active = true;
    }, { passive: true });

    frame.addEventListener('touchend', (e) => {
      if (!active) return;
      active = false;
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx   = endX - startX;
      const dy   = endY - startY;
      if (Math.abs(dy) > VERTICAL_TOLERANCE) return;
      if (Math.abs(dx) < THRESHOLD) return;
      if (dx > 0) { this.prev(); } else { this.next(); }
    }, { passive: true });
  }

  /**
   * Bind ArrowLeft / ArrowRight keyboard nav when the gallery element has focus.
   * Focus must be on or inside the gallery region for this to fire.
   */
  _bindKeyboard() {
    this.el.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this.prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
    });
  }

  /**
   * Show slide at idx; hide + pause-video all others.
   * @param {number} idx
   */
  goTo(idx) {
    if (idx < 0 || idx >= this.totalItems) return;
    this.currentIdx = idx;

    // Slide toggle — show active, hide + pause all others
    this.slides.forEach((slide, i) => {
      const isActive = i === idx;
      slide.classList.toggle('gez-pi-gallery__slide--active', isActive);
      if (isActive) {
        slide.hidden = false;
      } else {
        slide.hidden = true;
        // Pause any playing video in a now-hidden slide
        const vid = slide.querySelector('video');
        if (vid && !vid.paused) vid.pause();
      }
    });

    // Update dots
    this.dots.forEach((dot, i) => {
      const isActive = i === idx;
      dot.classList.toggle('gez-pi-gallery__dot--active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Update thumbs + scroll active thumb into view (desktop strip can overflow horizontally
    // when product has many media items; auto-scroll keeps the active thumb visible). 2026-05-16
    let activeThumb = null;
    this.thumbs.forEach((thumb, i) => {
      const isActive = i === idx;
      thumb.classList.toggle('gez-pi-gallery__thumb--active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      if (isActive) activeThumb = thumb;
    });
    if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
      activeThumb.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }
}

/* ============================================================
   3. KIT SIZE VARIANT SELECTOR
   Reads .gez-pi__size-card[data-variant-id] cards.
   On selection: updates URL, gallery, price row, ATC button,
   fires gez:variant-changed for downstream sections.
   ============================================================ */

class GezKitSizeSelector {
  /**
   * @param {HTMLElement} wrapper - .gez-pi-wrapper
   * @param {object} gallery - GezGalleryCoordinator instance
   * @param {Function|null} onSelect - optional callback fired after selection
   */
  constructor(wrapper, gallery, onSelect) {
    this.wrapper  = wrapper;
    this.gallery  = gallery;
    this.onSelect = onSelect ?? null;
    this.grid     = wrapper.querySelector('.gez-pi__size-grid');
    if (!this.grid) return;

    this.cards    = Array.from(this.grid.querySelectorAll('.gez-pi__size-card'));
    this.priceEl  = wrapper.querySelector('#gez-pi-price-now');
    this.wasEl    = wrapper.querySelector('#gez-pi-price-was');
    this.pillEl   = wrapper.querySelector('#gez-pi-save-pill');
    this.ctaLabel = wrapper.querySelector('.gez-pi__cta-label');
    this.variantInput = wrapper.querySelector('input.product-variant-id');
    this.atcBtn   = wrapper.querySelector('[id^="gez-pi-atc-"]');
    this._bind();
  }

  _bind() {
    this.cards.forEach((card) => {
      card.addEventListener('click', () => this._select(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._select(card);
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          const next = this.cards[this.cards.indexOf(card) + 1];
          if (next) { next.focus(); this._select(next); }
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = this.cards[this.cards.indexOf(card) - 1];
          if (prev) { prev.focus(); this._select(prev); }
        }
      });
    });
  }

  _select(card) {
    const variantId   = card.dataset.variantId;
    const priceRaw    = card.dataset.variantPrice;
    const compareRaw  = card.dataset.variantCompare;
    const available   = card.dataset.variantAvailable === 'true';

    // Visual state
    this.cards.forEach((c) => {
      c.classList.remove('gez-pi__size-card--selected');
      c.setAttribute('aria-checked', 'false');
      c.setAttribute('tabindex', '-1');
    });
    card.classList.add('gez-pi__size-card--selected');
    card.setAttribute('aria-checked', 'true');
    card.setAttribute('tabindex', '0');

    // Update hidden form input
    if (this.variantInput) {
      this.variantInput.value = variantId;
      this.variantInput.disabled = !available;
    }

    // Update ATC button availability
    if (this.atcBtn) {
      this.atcBtn.disabled = !available;
    }

    // Update price display
    if (this.priceEl && priceRaw) {
      this.priceEl.textContent = this._formatMoney(parseInt(priceRaw, 10));
    }
    if (compareRaw && parseInt(compareRaw, 10) > parseInt(priceRaw, 10)) {
      if (this.wasEl) {
        this.wasEl.textContent = this._formatMoney(parseInt(compareRaw, 10));
        this.wasEl.hidden = false;
      }
      if (this.pillEl) {
        const savePct = Math.round((parseInt(compareRaw, 10) - parseInt(priceRaw, 10)) / parseInt(compareRaw, 10) * 100);
        this.pillEl.hidden = false;
        this.pillEl.textContent = `SAVE ${savePct}%`;
      }
    } else {
      if (this.wasEl) this.wasEl.hidden = true;
      if (this.pillEl) this.pillEl.hidden = true;
    }

    // Update URL without navigation
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    } catch (_) { /* non-blocking */ }

    // Swap gallery to variant featured media
    const galleryIdx = this._findMediaIndexForVariant(variantId);
    if (galleryIdx >= 0 && this.gallery) {
      this.gallery.goTo(galleryIdx);
    }

    // Fire cross-section event
    document.dispatchEvent(new CustomEvent('gez:variant-changed', {
      bubbles: true,
      detail: {
        variantId,
        variantTitle: card.querySelector('.gez-pi__size-title')?.textContent?.trim() ?? '',
        price: parseInt(priceRaw, 10),
        available,
      },
    }));

    // Notify coordinator so ATC label can update
    if (this.onSelect) this.onSelect();
  }

  /**
   * Returns the currently selected size card, or null if none.
   * @returns {HTMLElement|null}
   */
  selectedCard() {
    return this.grid ? this.grid.querySelector('.gez-pi__size-card--selected') : null;
  }

  /**
   * Returns true when the currently selected variant title contains "deluxe" (case-insensitive).
   * @returns {boolean}
   */
  isDeluxeSelected() {
    const card = this.selectedCard();
    if (!card) return false;
    const title = card.querySelector('.gez-pi__size-title')?.textContent?.trim() ?? '';
    return title.toLowerCase().includes('deluxe');
  }

  /**
   * Returns the gallery media index that matches the variant's featured_media.
   * Relies on data-variant-id + data-idx attributes emitted by the Liquid template
   * on each gallery dot. The Liquid loops pi.variants and matches variant.featured_media.id
   * against each media item; the matching dot gets data-variant-id="{{ variant.id }}".
   *
   * If no dot carries data-variant-id for this variantId (i.e. the variant has no
   * featured_media set in Shopify Admin), returns -1 so the caller skips the gallery jump.
   *
   * @param {string} variantId
   * @returns {number} media index (>= 0), or -1 if no match found
   */
  _findMediaIndexForVariant(variantId) {
    const dots = this.wrapper.querySelectorAll('.gez-pi-gallery__dot[data-variant-id]');
    for (const dot of dots) {
      if (dot.dataset.variantId === variantId) {
        const idx = parseInt(dot.dataset.idx, 10);
        return isNaN(idx) ? -1 : idx;
      }
    }
    return -1;
  }

  /** Converts Shopify cents integer to formatted string */
  _formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }
}

/* ============================================================
   4. PACK QUANTITY SELECTOR
   Visual selection + cart quantity update.
   ATC label update delegated to coordinator via onSelect callback.
   Cart-side tier discount enforcement requires a Shopify Function
   (separate dispatch — not implemented here).
   ============================================================ */

class GezPackSelector {
  /**
   * @param {HTMLElement} wrapper - .gez-pi-wrapper
   * @param {Function|null} onSelect - optional callback fired after selection
   */
  constructor(wrapper, onSelect) {
    this.wrapper  = wrapper;
    this.onSelect = onSelect ?? null;
    this.grid     = wrapper.querySelector('.gez-pi__pack-grid');
    if (!this.grid) return;

    this.cards   = Array.from(this.grid.querySelectorAll('.gez-pi__pack-card'));
    this._bind();
  }

  _bind() {
    this.cards.forEach((card) => {
      card.addEventListener('click', () => this._select(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._select(card);
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          const next = this.cards[this.cards.indexOf(card) + 1];
          if (next) { next.focus(); this._select(next); }
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = this.cards[this.cards.indexOf(card) - 1];
          if (prev) { prev.focus(); this._select(prev); }
        }
      });
    });
  }

  _select(card) {
    this.cards.forEach((c) => {
      c.classList.remove('gez-pi__pack-card--selected');
      c.setAttribute('aria-checked', 'false');
      c.setAttribute('tabindex', '-1');
    });
    card.classList.add('gez-pi__pack-card--selected');
    card.setAttribute('aria-checked', 'true');
    card.setAttribute('tabindex', '0');

    // Update the cart quantity input — line quantity submitted to cart on ATC
    const qty = parseInt(card.dataset.tierQty, 10);
    const qtyInput = this.wrapper.querySelector('[data-gez-pi-qty]');
    if (qtyInput && qty > 0) {
      qtyInput.value = qty;
    }

    // Hide Shop Pay (dynamic checkout) when pack tier qty > 1.
    // Shop Pay reads name="id" but ignores name="quantity", so it would
    // charge for 1 unit at full price when a multi-pack tier is selected.
    // Hiding it prevents wrong-price accelerated checkout until the
    // Shopify Cart Transform Function is deployed (separate workstream).
    const checkoutWrapper = this.wrapper.querySelector('[data-gez-show-when-tier]');
    const checkoutNote    = this.wrapper.querySelector('.gez-pi__dynamic-checkout-note');
    if (checkoutWrapper) {
      const tierThreshold = parseInt(checkoutWrapper.dataset.gezShowWhenTier, 10) || 1;
      const isMultiPack   = qty > tierThreshold;
      checkoutWrapper.classList.toggle('gez-pi__dynamic-checkout--hidden', isMultiPack);
      if (checkoutNote) {
        checkoutNote.classList.toggle('gez-pi__dynamic-checkout-note--hidden', !isMultiPack);
      }
    }

    // Notify coordinator so ATC label can update
    if (this.onSelect) this.onSelect();
  }

  /**
   * Returns the currently selected pack card, or null if none.
   * @returns {HTMLElement|null}
   */
  selectedCard() {
    return this.grid ? this.grid.querySelector('.gez-pi__pack-card--selected') : null;
  }
}

/* ============================================================
   5. HEART / FAVORITE TOGGLE
   Persists to localStorage. Key: gez_favorites (JSON array of product IDs).
   ============================================================ */

class GezFavoriteButton {
  /**
   * @param {HTMLElement} btn - .gez-pi-gallery__heart button
   * @param {string} productId
   */
  constructor(btn, productId) {
    this.btn       = btn;
    this.productId = String(productId);
    this.storageKey = 'gez_favorites';
    this._syncState();
    btn.addEventListener('click', () => this._toggle());
  }

  _getFavorites() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) ?? '[]');
    } catch (_) { return []; }
  }

  _setFavorites(arr) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(arr));
    } catch (_) { /* non-blocking */ }
  }

  _syncState() {
    const isFav = this._getFavorites().includes(this.productId);
    this.btn.setAttribute('aria-pressed', isFav ? 'true' : 'false');
    this.btn.classList.toggle('gez-pi-gallery__heart--active', isFav);
  }

  _toggle() {
    const favs = this._getFavorites();
    const idx  = favs.indexOf(this.productId);
    if (idx >= 0) {
      favs.splice(idx, 1);
    } else {
      favs.push(this.productId);
    }
    this._setFavorites(favs);
    this._syncState();

    document.dispatchEvent(new CustomEvent('gez:favorite-toggled', {
      bubbles: true,
      detail: { productId: this.productId, favorited: idx < 0 },
    }));
  }
}

/* ============================================================
   SHARED MONEY FORMATTER
   Converts Shopify cents integer to formatted USD string.
   Used by ATC label coordinator and tier display updater.
   ============================================================ */

/**
 * @param {number} cents - integer cents (Shopify price format)
 * @returns {string} e.g. "$54.50"
 */
function formatMoney(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

/* ============================================================
   TIER PRICE COMPUTATION
   All displayed pack prices are computed from:
     variant base price (cents) × tier discount %
   No hardcoded price data attributes on pack cards.
   Cart-side enforcement requires Shopify Function (separate dispatch).
   ============================================================ */

/**
 * Computes the total price for a tier.
 * @param {number} variantPriceCents - base variant price in cents
 * @param {number} tierPctOff - discount percentage (e.g. 8.4034)
 * @param {number} tierQty - number of units in this pack tier
 * @returns {number} total cents, rounded to nearest cent
 */
function computeTierTotal(variantPriceCents, tierPctOff, tierQty) {
  const unitCents = Math.round(variantPriceCents * (100 - tierPctOff) / 100);
  return unitCents * tierQty;
}

/**
 * Recomputes and writes each-val and total-val for every pack card in the grid.
 * Called on variant change (size card selected) — base price changed.
 * @param {HTMLElement} wrapper - .gez-pi-wrapper
 * @param {GezKitSizeSelector|null} kitSelector
 */
function updateTierDisplays(wrapper, kitSelector) {
  const packGrid = wrapper.querySelector('.gez-pi__pack-grid');
  if (!packGrid) return;

  const sizeCard = kitSelector ? kitSelector.selectedCard() : null;

  let variantPriceCents;
  if (sizeCard) {
    // Kit-size-card product (FSK / multi-variant) — price comes from the selected size card.
    variantPriceCents = parseInt(sizeCard.dataset.variantPrice, 10);
  } else if (!wrapper.querySelector('.gez-pi__color-picker')) {
    // Single-variant product with no size grid and no color-swatch picker.
    // Price is stamped on the wrapper by Liquid via data-base-price-cents.
    variantPriceCents = parseInt(wrapper.dataset.basePriceCents, 10);
  } else {
    // Color-swatch product — the color-swatch IIFE owns price updates. Do not overwrite.
    return;
  }

  if (!variantPriceCents) return;

  packGrid.querySelectorAll('.gez-pi__pack-card').forEach((card) => {
    const tierQty    = parseInt(card.dataset.tierQty, 10);
    const tierPctOff = parseFloat(card.dataset.tierPctOff);
    if (!tierQty || isNaN(tierPctOff)) return;

    const unitCents  = Math.round(variantPriceCents * (100 - tierPctOff) / 100);
    const totalCents = unitCents * tierQty;

    const eachValEl  = card.querySelector('.gez-pi__pack-each-val');
    const totalValEl = card.querySelector('.gez-pi__pack-total-val');
    if (eachValEl)  eachValEl.textContent  = formatMoney(unitCents);
    if (totalValEl) totalValEl.textContent = formatMoney(totalCents);
  });
}

/* ============================================================
   ATC LABEL COORDINATOR
   Reads the selected kit-size and pack-tier to compute and write
   the ATC button label. Called on init and after either selector changes.

   Displayed total = variant base price × tier discount % × tier qty.
   The actual cart total will be qty × base-variant-price until the
   Shopify Cart Transform Function is deployed (separate dispatch).
   ============================================================ */

/**
 * Updates the ATC button label text based on the currently selected
 * kit-size variant and pack tier, computing the price from:
 *   variant base price × (100 - tier_pct_off) / 100 × tier_qty
 *
 * @param {HTMLElement} wrapper - .gez-pi-wrapper
 * @param {GezKitSizeSelector|null} kitSelector
 * @param {GezPackSelector|null} packSelector
 */
function updateAtcLabel(wrapper, kitSelector, packSelector) {
  const atcBtn  = wrapper.querySelector('[id^="gez-pi-atc-"]');
  const ctaLabel = wrapper.querySelector('.gez-pi__cta-label');
  if (!atcBtn || !ctaLabel) return;

  // If sold out, label is already handled by Liquid server-render. Do not overwrite.
  if (atcBtn.disabled) return;

  const template = atcBtn.dataset.labelTemplate ?? 'Add to Cart - {price}';

  const sizeCard = kitSelector ? kitSelector.selectedCard() : null;
  const packCard = packSelector ? packSelector.selectedCard() : null;

  let variantPriceCents;
  if (sizeCard) {
    // Kit-size-card product (FSK / multi-variant) — price from selected size card.
    variantPriceCents = parseInt(sizeCard.dataset.variantPrice, 10);
  } else if (!wrapper.querySelector('.gez-pi__color-picker')) {
    // Single-variant product (Eco-Clean, Exo-Clean) — no size grid, no color picker.
    // Liquid stamps the current variant price on the wrapper as data-base-price-cents.
    variantPriceCents = parseInt(wrapper.dataset.basePriceCents, 10);
  } else {
    // Color-swatch product (Surface Shield) — color-swatch IIFE owns the ATC label.
    // Do not overwrite.
    return;
  }

  const tierQty    = packCard ? parseInt(packCard.dataset.tierQty, 10)       : 1;
  const tierPctOff = packCard ? parseFloat(packCard.dataset.tierPctOff)      : 0;

  let label;
  if (variantPriceCents && tierQty) {
    const totalCents = computeTierTotal(variantPriceCents, tierPctOff || 0, tierQty);
    label = template.replace('{price}', formatMoney(totalCents));
  } else {
    label = template.replace(' - {price}', '').replace('{price}', '');
  }

  ctaLabel.textContent = label;
}

/* ============================================================
   6. GALLERY LIGHTBOX (Phase 12)
   Full-screen overlay that clones the media slide stack for high-res viewing.
   Scoped to the section wrapper. Syncs currentIdx with GezGalleryCoordinator
   on open and close.
   ============================================================ */

class GezGalleryLightbox {
  /**
   * @param {HTMLElement} wrapper - .gez-pi-wrapper
   * @param {GezGalleryCoordinator} gallery - main gallery ref for state sync
   */
  constructor(wrapper, gallery) {
    this.wrapper    = wrapper;
    this.gallery    = gallery;
    this.el         = wrapper.querySelector('.gez-pi-lightbox');
    if (!this.el) return;

    this.slides     = Array.from(this.el.querySelectorAll('.gez-pi-lightbox__slide'));
    this.dotEls     = Array.from(this.el.querySelectorAll('.gez-pi-lightbox__dot'));
    this.currentIdx = 0;
    this.totalItems = this.slides.length;
    this.triggerEl  = null;

    this._bindTrigger();
    this._bindClose();
    this._bindArrows();
    this._bindKeyboard();
    this._bindSwipe();
    this._bindBackdrop();
    this._bindDots();
  }

  /** Open at a specific slide index, syncing with main gallery. */
  open(startIdx) {
    if (!this.el) return;
    const idx = (typeof startIdx === 'number' && startIdx >= 0 && startIdx < this.totalItems)
      ? startIdx : 0;
    this._showSlide(idx);
    this.triggerEl = document.activeElement;
    this.el.hidden = false;
    document.body.classList.add('gez-pi-lightbox-open');
    const closeBtn = this.el.querySelector('[data-lightbox-action="close"]');
    if (closeBtn) closeBtn.focus();
  }

  /** Close the lightbox; pause any playing media; return focus to trigger. */
  close() {
    if (!this.el) return;
    this._pauseAllVideos();
    this.el.hidden = true;
    document.body.classList.remove('gez-pi-lightbox-open');
    // Sync final idx back to main gallery so it reflects the last lightbox position.
    if (this.gallery && typeof this.gallery.goTo === 'function') {
      this.gallery.goTo(this.currentIdx);
    }
    if (this.triggerEl && typeof this.triggerEl.focus === 'function') {
      this.triggerEl.focus();
    }
  }

  next() {
    if (this.totalItems < 2) return;
    this._showSlide((this.currentIdx + 1) % this.totalItems);
  }

  prev() {
    if (this.totalItems < 2) return;
    this._showSlide((this.currentIdx - 1 + this.totalItems) % this.totalItems);
  }

  /** Toggle slide visibility + update dots. */
  _showSlide(idx) {
    this.currentIdx = idx;
    this.slides.forEach((slide, i) => {
      const active = i === idx;
      slide.classList.toggle('gez-pi-lightbox__slide--active', active);
      slide.hidden = !active;
      if (!active) {
        const vid = slide.querySelector('video');
        if (vid && !vid.paused) vid.pause();
      }
    });
    this.dotEls.forEach((dot, i) => {
      const active = i === idx;
      dot.classList.toggle('gez-pi-lightbox__dot--active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  _pauseAllVideos() {
    this.slides.forEach((slide) => {
      const vid = slide.querySelector('video');
      if (vid && !vid.paused) vid.pause();
    });
  }

  /** Wire the transparent overlay button and any other [data-lightbox-open] triggers. */
  _bindTrigger() {
    const triggers = this.wrapper.querySelectorAll('[data-lightbox-open]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const startIdx = (this.gallery && typeof this.gallery.currentIdx === 'number')
          ? this.gallery.currentIdx : 0;
        this.open(startIdx);
      });
    });
  }

  _bindClose() {
    const closeBtn = this.el.querySelector('[data-lightbox-action="close"]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
  }

  _bindArrows() {
    const prevBtn = this.el.querySelector('[data-lightbox-arrow="prev"]');
    const nextBtn = this.el.querySelector('[data-lightbox-arrow="next"]');
    if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
    if (nextBtn) nextBtn.addEventListener('click', () => this.next());
  }

  /** Document-level keyboard handler — only fires when lightbox is open. */
  _bindKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (!this.el || this.el.hidden) return;
      if (e.key === 'Escape')     { e.preventDefault(); this.close(); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this.prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
    });
  }

  /** Touch swipe — horizontal threshold 50px, vertical tolerance 60px. */
  _bindSwipe() {
    const stage = this.el.querySelector('[data-lightbox-swipeable]');
    if (!stage) return;
    let startX = 0, startY = 0, active = false;
    const THRESHOLD = 50, VTOL = 60;
    stage.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      active = true;
    }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      if (!active) return;
      active = false;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dy) > VTOL) return;
      if (Math.abs(dx) < THRESHOLD) return;
      if (dx > 0) { this.prev(); } else { this.next(); }
    }, { passive: true });
  }

  /**
   * Click on the backdrop (lightbox el itself) or on a media element (cursor: zoom-out)
   * closes the lightbox.
   */
  _bindBackdrop() {
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el || e.target.classList.contains('gez-pi-lightbox__media')) {
        this.close();
      }
    });
  }

  /** Dot buttons: click to go to that slide. */
  _bindDots() {
    this.dotEls.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.idx, 10);
        if (!isNaN(idx)) this._showSlide(idx);
      });
    });
  }
}

/* ============================================================
   INIT — instantiate all controllers per section instance
   ============================================================ */

function initGezProductInformation(wrapper) {
  if (!wrapper) return;

  // Phase 10: GezGalleryCoordinator reads slides directly from the DOM.
  // No mediaItems array needed — all media elements are pre-rendered in Liquid.
  const gallery = new GezGalleryCoordinator(wrapper);

  // Phase 12: Lightbox — cloned slide stack, opens on hero click.
  new GezGalleryLightbox(wrapper, gallery);

  // Selectors are wired with callbacks that cross-notify.
  // kitSelector and packSelector are declared before construction so the
  // closures can reference each other once both are assigned.
  let kitSelector = null;
  let packSelector = null;

  // Variant change (size card) — recompute ALL tier displays then ATC label.
  const onVariantSelect = () => {
    updateTierDisplays(wrapper, kitSelector);
    updateAtcLabel(wrapper, kitSelector, packSelector);
  };

  // Tier change (pack card) — recompute ATC label only (tier displays are variant-dependent, not tier-dependent).
  const onPackSelect = () => updateAtcLabel(wrapper, kitSelector, packSelector);

  kitSelector  = new GezKitSizeSelector(wrapper, gallery, onVariantSelect);
  packSelector = new GezPackSelector(wrapper, onPackSelect);

  // Set correct initial label on page load (Liquid renders initial prices, but ATC label
  // also needs to reflect the same computation on client-side init).
  updateAtcLabel(wrapper, kitSelector, packSelector);

  // Sync Shop Pay visibility on page load — Liquid renders Tier 1 (Single) selected by default,
  // so dynamic checkout starts visible. If the URL carries ?variant= for a non-default selection,
  // or if Liquid ever changes the default, this ensures the state is always consistent on init.
  const initPackCard = packSelector ? packSelector.selectedCard() : null;
  if (initPackCard) {
    const checkoutWrapper = wrapper.querySelector('[data-gez-show-when-tier]');
    const checkoutNote    = wrapper.querySelector('.gez-pi__dynamic-checkout-note');
    if (checkoutWrapper) {
      const tierThreshold = parseInt(checkoutWrapper.dataset.gezShowWhenTier, 10) || 1;
      const initQty       = parseInt(initPackCard.dataset.tierQty, 10);
      const isMultiPack   = initQty > tierThreshold;
      checkoutWrapper.classList.toggle('gez-pi__dynamic-checkout--hidden', isMultiPack);
      if (checkoutNote) {
        checkoutNote.classList.toggle('gez-pi__dynamic-checkout-note--hidden', !isMultiPack);
      }
    }
  }

  // Shipping countdown
  const countdownEl = wrapper.querySelector('[data-countdown]');
  if (countdownEl) new GezShippingCountdown(countdownEl);

  // Heart button
  const heartBtn  = wrapper.querySelector('.gez-pi-gallery__heart');
  const productHandle = wrapper.dataset.productHandle;
  if (heartBtn && productHandle) new GezFavoriteButton(heartBtn, productHandle);
}

// Initialize on DOMContentLoaded or immediately if DOM is already ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.gez-pi-wrapper').forEach(initGezProductInformation);
  });
} else {
  document.querySelectorAll('.gez-pi-wrapper').forEach(initGezProductInformation);
}

// Shopify Theme Editor re-init: when a section is edited in the editor the DOM is
// re-rendered but DOMContentLoaded does not fire again. shopify:section:load covers this.
// Without this, JS works fine on the public URL but appears broken in the editor.
document.addEventListener('shopify:section:load', (event) => {
  const wrapper = event.target.querySelector('.gez-pi-wrapper');
  if (wrapper) initGezProductInformation(wrapper);
});
